import { DeleteOutlined, EditOutlined, SwapOutlined } from '@ant-design/icons';
import type { BaseColumn } from '@konnecta/generic-table';
import { Button, Space, Tag, Tooltip, Typography } from 'antd';
import { TFunction } from 'i18next';
import type { InventoryItemRow } from '../../types';
import { createdAtColumn } from '@app/components/common/createdAtColumn';

export interface InventoryTableHandlers {
  onEdit: (row: InventoryItemRow) => void;
  onDelete: (row: InventoryItemRow) => void;
  onRecordMovement: (row: InventoryItemRow) => void;
}

/** Small capability tags (Consumable / Sellable / Rentable). */
const renderCapabilities = (row: InventoryItemRow, t: TFunction) => {
  const tags: React.ReactNode[] = [];
  if (row.isConsumable) tags.push(<Tag key="c">{t('inventory-cap-consumable')}</Tag>);
  if (row.isSellable) tags.push(<Tag key="s">{t('inventory-cap-sellable')}</Tag>);
  if (row.isRentable) tags.push(<Tag key="r">{t('inventory-cap-rentable')}</Tag>);
  return tags.length ? <Space size={[4, 4]}>{tags}</Space> : <Typography>-</Typography>;
};

export const getInventoryItemsTableColumns = (
  t: TFunction,
  handlers: InventoryTableHandlers
): BaseColumn<InventoryItemRow>[] => [
  {
    id: 'name',
    header: t('inventory-col-name'),
    accessor: 'name',
    width: 240,
    sortable: false,
    copy: false,
    cellRenderer: (value) => <Typography>{(value as string) || '-'}</Typography>,
  },
  {
    id: 'category',
    header: t('inventory-col-category'),
    accessor: 'categoryName',
    width: 180,
    copy: false,
    cellRenderer: (value) => <Typography>{(value as string) || '-'}</Typography>,
  },
  {
    id: 'capabilities',
    header: t('inventory-col-capabilities'),
    accessor: 'itemId',
    // Explicit: the table right-aligns a column whose accessor holds a number,
    // which put this header over on the right while its tags render on the left.
    align: 'left',
    width: 220,
    sortable: false,
    filterable: false,
    copy: false,
    cellRenderer: (_value, row) => renderCapabilities(row, t),
  },
  {
    id: 'unit',
    header: t('inventory-col-unit'),
    accessor: 'unit',
    width: 110,
    copy: false,
    cellRenderer: (value) => <Typography>{(value as string) || '-'}</Typography>,
  },
  {
    // Left-aligned, unlike every other figure in the app. The table hands all
    // leftover table width to the last scrolling column, which is this one, and
    // a value pushed to the right of a 437px cell renders underneath the sticky
    // Actions column — the stock figure was invisible until you scrolled.
    id: 'onHand',
    header: t('inventory-col-on-hand'),
    accessor: 'onHand',
    align: 'left',
    width: 160,
    sortable: false,
    copy: false,
    cellRenderer: (value, row) => (
      <div className="flex items-center gap-2">
        <Typography className="tabular-nums" style={{ fontWeight: 600 }}>
          {value as number}
        </Typography>
        {row.lowStock && <Tag color="warning">{t('inventory-low-stock')}</Tag>}
      </div>
    ),
  },
  createdAtColumn<InventoryItemRow>(t),
  {
    // Icon buttons, not labelled links: three translated labels needed 280px of
    // sticky column, which covered the On hand figure they sit next to.
    id: 'actions',
    header: t('inventory-col-actions'),
    accessor: 'itemId',
    align: 'center',
    sticky: 'right',
    width: 130,
    sortable: false,
    filterable: false,
    copy: false,
    cellRenderer: (_value, row) => (
      // data-kgt-interactive opts the cell out of the row's click handlers, so a
      // double-click on Delete opens only the confirm and not the edit drawer too.
      // The table only applies its own `.action-column` guard to its built-in
      // actions dropdown, not to a hand-rolled actions column like this one.
      <Space size={4} data-kgt-interactive="true">
        <Tooltip title={t('inventory-record-movement')}>
          <Button
            type="text"
            size="small"
            icon={<SwapOutlined />}
            aria-label={t('inventory-record-movement')}
            onClick={() => handlers.onRecordMovement(row)}
          />
        </Tooltip>
        <Tooltip title={t('inventory-action-edit')}>
          <Button
            type="text"
            size="small"
            icon={<EditOutlined />}
            aria-label={t('inventory-action-edit')}
            onClick={() => handlers.onEdit(row)}
          />
        </Tooltip>
        <Tooltip title={t('inventory-action-delete')}>
          <Button
            type="text"
            size="small"
            danger
            icon={<DeleteOutlined />}
            aria-label={t('inventory-action-delete')}
            onClick={() => handlers.onDelete(row)}
          />
        </Tooltip>
      </Space>
    ),
  },
];
